// src/pages/errors/ErrorBrowserPage.jsx - Detailed Search & Browse
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';

// Hooks
import { useErrors } from '../hooks/useErrors';
import { useStaff } from '../../staff/hooks/useStaff';
import { useDepartments } from '../../departments/hooks/useDepartment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

// Components
import ErrorBrowserHeader from '../components/ErrorBrowserHeader';
import ErrorFilters from '../components/ErrorFilters';
import ErrorsTable from '../components/ErrorsTable';
import ErrorDetailsDrawer from '../components/ErrorDetailsDrawer';

export default function ErrorBrowserPage() {
  const location = useLocation();
  const { showError } = useSnackbar();

  // Get initial state from navigation
  const initialState = location.state || {};

  // State for filters and UI
  const [timeRange, setTimeRange] = useState(initialState.timeRange || 24);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedStaffMember, setSelectedStaffMember] = useState('');
  const [selectedEndpoint, setSelectedEndpoint] = useState(initialState.selectedEndpoint || '');
  const [selectedErrorCode, setSelectedErrorCode] = useState(initialState.selectedErrorCode || '');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showFilters, setShowFilters] = useState(true); // Show filters by default in browser
  const [searchQuery, setSearchQuery] = useState('');

  // Drawer state
  const [selectedError, setSelectedError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // View state
  const [currentView, setCurrentView] = useState('overview');

  // API hooks
  const {
    errorSummary,
    errors,
    errorDetails,
    loading,
    error,
    clearError,
    fetchErrorSummary,
    fetchErrorsByEndpoint,
    fetchErrorsByStaffMember,
    fetchErrorsByDepartment,
    fetchErrorDetails,
  } = useErrors();

  const {
    staff,
    fetchDepartmentStaff,
  } = useStaff();

  const {
    departments,
    fetchAllDepartments,
  } = useDepartments();

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchErrorSummary({ hours: timeRange }),
        fetchAllDepartments({ include_deactivated: false }),
        fetchDepartmentStaff({ include_inactive: true }),
      ]);
    };

    loadInitialData();
  }, []);

  // Auto-load errors when we have initial filters from navigation
  useEffect(() => {
    if (initialState.selectedEndpoint && errorSummary) {
      loadErrorsByEndpoint(initialState.selectedEndpoint);
    } else if (initialState.selectedErrorCode && errorSummary) {
      loadErrorsByCode(initialState.selectedErrorCode);
    }
  }, [errorSummary, initialState]);

  // Load errors based on current view and filters
  const loadErrors = useCallback(async () => {
    const baseParams = {
      hours: timeRange,
      limit: 100,
    };

    try {
      switch (currentView) {
        case 'by-endpoint':
          if (selectedEndpoint) {
            await fetchErrorsByEndpoint({
              ...baseParams,
              endpoint: selectedEndpoint,
            });
          }
          break;
        case 'by-staff':
          if (selectedStaffMember) {
            await fetchErrorsByStaffMember(selectedStaffMember, baseParams);
          }
          break;
        case 'by-department':
          if (selectedDepartment) {
            await fetchErrorsByDepartment(selectedDepartment, baseParams);
          }
          break;
        default:
          // Load recent errors for overview
          if (errorSummary?.errors_by_endpoint) {
            const topEndpoint = Object.entries(errorSummary.errors_by_endpoint)
              .sort(([,a], [,b]) => b - a)[0]?.[0];
            if (topEndpoint) {
              await fetchErrorsByEndpoint({
                ...baseParams,
                endpoint: topEndpoint,
              });
            }
          }
          break;
      }
    } catch (err) {
      showError('Failed to load error data');
    }
  }, [
    currentView,
    timeRange,
    selectedEndpoint,
    selectedStaffMember,
    selectedDepartment,
    errorSummary,
    fetchErrorsByEndpoint,
    fetchErrorsByStaffMember,
    fetchErrorsByDepartment,
    showError,
  ]);

  // Helper functions for specific loading
  const loadErrorsByEndpoint = useCallback(async (endpoint) => {
    try {
      await fetchErrorsByEndpoint({
        hours: timeRange,
        limit: 100,
        endpoint: endpoint,
      });
      setCurrentView('by-endpoint');
      setSelectedEndpoint(endpoint);
    } catch (err) {
      showError('Failed to load errors for endpoint');
    }
  }, [timeRange, fetchErrorsByEndpoint, showError]);

  const loadErrorsByCode = useCallback(async (errorCode) => {
    try {
      // For error code, we'll load from the most frequent endpoint with that code
      if (errorSummary?.errors_by_endpoint) {
        const topEndpoint = Object.entries(errorSummary.errors_by_endpoint)
          .sort(([,a], [,b]) => b - a)[0]?.[0];
        if (topEndpoint) {
          await fetchErrorsByEndpoint({
            hours: timeRange,
            limit: 100,
            endpoint: topEndpoint,
          });
          setSelectedErrorCode(errorCode);
        }
      }
    } catch (err) {
      showError('Failed to load errors for error code');
    }
  }, [timeRange, errorSummary, fetchErrorsByEndpoint, showError]);

  // Load errors when view or filters change
  useEffect(() => {
    if (errorSummary) {
      loadErrors();
    }
  }, [currentView, loadErrors, errorSummary]);

  // Filter errors based on search and additional filters
  const filteredErrors = useMemo(() => {
    let filtered = [...(errors || [])];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(error =>
        error.error_message?.toLowerCase().includes(query) ||
        error.error_code?.toLowerCase().includes(query) ||
        error.endpoint?.toLowerCase().includes(query)
      );
    }

    if (selectedErrorCode) {
      filtered = filtered.filter(error => error.error_code === selectedErrorCode);
    }

    if (startDate && endDate) {
      filtered = filtered.filter(error => {
        const errorDate = new Date(error.occurred_at);
        return errorDate >= startDate && errorDate <= endDate;
      });
    }

    return filtered.sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));
  }, [errors, searchQuery, selectedErrorCode, startDate, endDate]);

  // Get unique error codes and endpoints for filters
  const uniqueErrorCodes = useMemo(() => {
    if (!errorSummary?.errors_by_code) return [];
    return Object.keys(errorSummary.errors_by_code).sort();
  }, [errorSummary]);

  const uniqueEndpoints = useMemo(() => {
    if (!errorSummary?.errors_by_endpoint) return [];
    return Object.keys(errorSummary.errors_by_endpoint).sort();
  }, [errorSummary]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchErrorSummary({ 
      hours: timeRange,
      department_id: selectedDepartment || undefined,
    });
    loadErrors();
  }, [timeRange, selectedDepartment, fetchErrorSummary, loadErrors]);

  const handleToggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedDepartment('');
    setSelectedStaffMember('');
    setSelectedEndpoint('');
    setSelectedErrorCode('');
    setStartDate(null);
    setEndDate(null);
    setSearchQuery('');
    setCurrentView('overview');
  }, []);

  const handleViewChange = useCallback((newView) => {
    setCurrentView(newView);
    // Clear related filters when changing views
    if (newView !== 'by-endpoint') setSelectedEndpoint('');
    if (newView !== 'by-staff') setSelectedStaffMember('');
    if (newView !== 'by-department') setSelectedDepartment('');
  }, []);

  const handleErrorClick = useCallback(async (errorItem) => {
    try {
      await fetchErrorDetails(errorItem.error_id);
      setSelectedError(errorItem);
      setIsDrawerOpen(true);
    } catch (err) {
      showError('Failed to load error details');
    }
  }, [fetchErrorDetails, showError]);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedError(null);
  }, []);

  const handleLoadDetailedErrors = useCallback(async (type, value) => {
    const baseParams = {
      hours: timeRange,
      limit: 100,
    };

    try {
      switch (type) {
        case 'endpoint':
          await fetchErrorsByEndpoint({
            ...baseParams,
            endpoint: value,
          });
          setCurrentView('by-endpoint');
          setSelectedEndpoint(value);
          break;
        case 'staff':
          await fetchErrorsByStaffMember(value, baseParams);
          setCurrentView('by-staff');
          setSelectedStaffMember(value);
          break;
        case 'department':
          await fetchErrorsByDepartment(value, baseParams);
          setCurrentView('by-department');
          setSelectedDepartment(value);
          break;
        default:
          break;
      }
    } catch (err) {
      showError('Failed to load detailed errors');
    }
  }, [timeRange, fetchErrorsByEndpoint, fetchErrorsByStaffMember, fetchErrorsByDepartment, showError]);

  // Compute active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedDepartment) count++;
    if (selectedStaffMember) count++;
    if (selectedEndpoint) count++;
    if (selectedErrorCode) count++;
    if (startDate && endDate) count++;
    if (searchQuery) count++;
    return count;
  }, [selectedDepartment, selectedStaffMember, selectedEndpoint, selectedErrorCode, startDate, endDate, searchQuery]);

  return (
    <Box>
      {/* Header */}
      <ErrorBrowserHeader
        onRefresh={handleRefresh}
        onToggleFilters={handleToggleFilters}
        loading={loading}
        activeFiltersCount={activeFiltersCount}
        currentView={currentView}
        onViewChange={handleViewChange}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        totalErrors={filteredErrors.length}
        totalInTimeRange={errorSummary?.total_errors || 0}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <ErrorFilters
        show={showFilters}
        searchQuery={searchQuery}
        selectedDepartment={selectedDepartment}
        selectedStaffMember={selectedStaffMember}
        selectedEndpoint={selectedEndpoint}
        selectedErrorCode={selectedErrorCode}
        startDate={startDate}
        endDate={endDate}
        departments={departments}
        staff={staff}
        errorCodes={uniqueErrorCodes}
        endpoints={uniqueEndpoints}
        currentView={currentView}
        onSearchChange={setSearchQuery}
        onDepartmentChange={setSelectedDepartment}
        onStaffMemberChange={setSelectedStaffMember}
        onEndpointChange={setSelectedEndpoint}
        onErrorCodeChange={setSelectedErrorCode}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onClearFilters={handleClearFilters}
      />

      {/* Errors Table */}
      <ErrorsTable
        errors={filteredErrors}
        loading={loading}
        currentView={currentView}
        errorSummary={errorSummary}
        onErrorClick={handleErrorClick}
        onRefresh={loadErrors}
        onLoadDetailedErrors={handleLoadDetailedErrors}
      />

      {/* Error Details Drawer */}
      <ErrorDetailsDrawer
        open={isDrawerOpen}
        error={selectedError}
        errorDetails={errorDetails}
        loading={loading}
        onClose={handleCloseDrawer}
      />
    </Box>
  );
}